import { useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";
import { HiLockClosed, HiOutlinePlus } from "react-icons/hi2";
import { FaTrash } from "react-icons/fa";
import { Slider } from "../Slider";
import { Modal } from "../Modal";
import { Box } from "../Box";
import { Flex } from "../Flex";
import { Button } from "../Button";
import { IconButton } from "../IconButton";
import { Input } from "../Input";
import { H2 } from "../Heading";
import { Paragraph } from "../Paragraph";
import Select, { Option } from "../Select";
import { useDivisions } from "../../data/useDivisions";
import { useTeams } from "../../data/useTeams";
import { AddShareForm } from "./AddShareForm";
import {
  AccessLevel,
  LEVEL_LABEL,
  NamedUnit,
  NewShare,
  Share,
} from "./types";

/** Above this many shares the list gains a search box. */
const LONG_LIST_THRESHOLD = 6;

/**
 * One Division in the Shared with list, with the shared Teams under it.
 * `share` is unset when the Division itself isn't an additional share — the
 * record's primary Division, whose other Teams can still be shared into.
 */
interface ShareGroup {
  key: string;
  name: string;
  isPrimary: boolean;
  share?: Share;
  teams: Share[];
}

const plural = (n: number, word: string) =>
  `${n} ${word}${n === 1 ? "" : "s"}`;

const LEVEL_OPTIONS: Option[] = [
  { label: LEVEL_LABEL.view, value: "view" },
  { label: LEVEL_LABEL.edit, value: "edit" },
];

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <Paragraph
    m={0}
    fontSize={0}
    fontWeight={600}
    letterSpacing="0.04em"
    color="gray.50"
  >
    {children}
  </Paragraph>
);

const StyledLevelPill = styled.span<{ level: AccessLevel }>`
  ${({ level, theme: { colors, radii, space, fontSizes, fontWeights } }) => `
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
    height: 24px;
    padding: 0 ${space[1]};
    box-sizing: border-box;
    white-space: nowrap;
    border-radius: ${radii[0]};
    font-family: "Inter", sans-serif;
    font-size: ${fontSizes[0]};
    font-weight: ${fontWeights.medium};
    background: ${level === "edit" ? colors.blue[50] : colors.background};
    color: ${level === "edit" ? colors.blue.primary : colors.gray[60]};
    border: 1px solid ${level === "edit" ? colors.blue[50] : colors.gray[20]};
  `}
`;

const LevelPill = ({ level }: { level: AccessLevel }) => (
  <StyledLevelPill level={level}>{LEVEL_LABEL[level]}</StyledLevelPill>
);

export interface AccessPanelProps {
  isOpen: boolean;
  onClose: () => void;
  recordName: string;
  recordReference?: string;
  /** "profile", "monitoring record", "application"… — used in copy. */
  noun: string;
  primaryDivision?: NamedUnit | null;
  primaryTeam?: NamedUnit | null;
  shares: Share[];
  /** Holds the module's `*.add_division` / `*.add_team` permission. */
  canShare: boolean;
  /** Set when the viewer only sees this record through a share. */
  viewerLevel?: AccessLevel;
  /** Viewer's own Team ids — marks "· your team" on matching rows. */
  viewerTeamIds?: number[];
  /**
   * Write callbacks. Each control only renders once its callback is wired,
   * so the panel is read-only until the sharing endpoints exist.
   */
  /** Called with every Division/Team picked in one submit of the add form. */
  onAdd?: (shares: NewShare[]) => void;
  onChangeLevel?: (share: Share, level: AccessLevel) => void;
  /** Removing a Division passes its shared Teams along with it. */
  onRemove?: (shares: Share[]) => void;
  /** Offer View only / Can edit when adding. Off until the API stores levels. */
  supportsLevels?: boolean;
  isSaving?: boolean;
}

/**
 * Right-hand Access panel: locked Primary Division + Team at the top, then the
 * "Shared with" list — each shared Division with its shared Teams nested under
 * it. Opened from AccessButton; shared by every record detail page.
 */
export const AccessPanel = ({
  isOpen,
  onClose,
  recordName,
  recordReference,
  noun,
  primaryDivision,
  primaryTeam,
  shares,
  canShare,
  viewerLevel,
  viewerTeamIds = [],
  onAdd,
  onChangeLevel,
  onRemove,
  supportsLevels = false,
  isSaving,
}: AccessPanelProps) => {
  const { colors } = useTheme() as any;
  const [isAdding, setIsAdding] = useState(false);
  const [removing, setRemoving] = useState<{
    name: string;
    shares: Share[];
  } | null>(null);
  const [search, setSearch] = useState("");

  const isGrantee = !!viewerLevel;
  const canManage = canShare && !isGrantee;
  const noPermissionTitle = canManage
    ? undefined
    : "You don't have permission to change sharing";
  const isLongList = shares.length > LONG_LIST_THRESHOLD;

  const primaryLabel = [primaryDivision?.display_name, primaryTeam?.display_name]
    .filter(Boolean)
    .join(" · ");

  // Record responses don't say which Division a shared Team sits in, so the
  // tenant lists (cached, shared with the add form) supply it.
  const hasTeamShares = shares.some((s) => s.kind === "team");
  const teamsQuery = useTeams(undefined, isOpen && hasTeamShares);
  const divisionsQuery = useDivisions();

  const groups = useMemo<ShareGroup[]>(() => {
    const teamDivision = new Map(
      (teamsQuery.data?.data ?? []).map((t) => [t.id, t.division]),
    );
    const divisionName = new Map(
      (divisionsQuery.data?.data ?? []).map((d) => [d.id, d.display_name]),
    );
    const byKey = new Map<string, ShareGroup>();
    const groupFor = (divisionId?: number) => {
      const key = divisionId ? String(divisionId) : "unknown";
      if (!byKey.has(key)) {
        const isPrimary = !!divisionId && divisionId === primaryDivision?.id;
        byKey.set(key, {
          key,
          isPrimary,
          name: !divisionId
            ? "Teams"
            : isPrimary
            ? primaryDivision?.display_name ?? ""
            : divisionName.get(divisionId) ?? "",
          teams: [],
        });
      }
      return byKey.get(key);
    };

    shares
      .filter((s) => s.kind === "division")
      .forEach((s) => {
        const group = groupFor(s.id);
        group.share = s;
        group.name = s.name;
      });
    shares
      .filter((s) => s.kind === "team")
      .forEach((s) => groupFor(teamDivision.get(s.id)).teams.push(s));

    return Array.from(byKey.values());
  }, [shares, teamsQuery.data, divisionsQuery.data, primaryDivision]);

  const visibleGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!isLongList || !term) return groups;
    return groups
      .map((g) =>
        g.name.toLowerCase().includes(term)
          ? g
          : {
              ...g,
              teams: g.teams.filter((t) =>
                t.name.toLowerCase().includes(term),
              ),
            },
      )
      .filter((g) => g.name.toLowerCase().includes(term) || g.teams.length);
  }, [groups, isLongList, search]);

  const divisionCount = shares.filter((s) => s.kind === "division").length;
  const teamCount = shares.length - divisionCount;

  const handleClose = () => {
    setIsAdding(false);
    setSearch("");
    onClose();
  };

  const levelControl = (share: Share) =>
    share.level &&
    (onChangeLevel && !isGrantee ? (
      <Select
        width="130px"
        flexShrink={0}
        size="regular"
        isInModal
        options={LEVEL_OPTIONS}
        value={LEVEL_OPTIONS.find((o) => o.value === share.level)}
        onChange={(o) =>
          onChangeLevel(share, (o as Option).value as AccessLevel)
        }
        isDisabled={!canManage || isSaving}
        title={noPermissionTitle}
      />
    ) : (
      <LevelPill level={share.level} />
    ));

  const removeButton = (name: string, toRemove: Share[]) =>
    onRemove &&
    !isGrantee && (
      <IconButton
        Type={FaTrash}
        size="14px"
        color="gray.50"
        isDisabled={!canManage}
        title={noPermissionTitle ?? "Stop sharing"}
        onClick={
          canManage ? () => setRemoving({ name, shares: toRemove }) : undefined
        }
      />
    );

  return (
    <>
      <Slider isOpen={isOpen} onRequestClose={handleClose} width="600px">
        <Flex flexDirection="column" height="100%">
          <Flex flexDirection="column" gap="22" flex={1} px={4} pt={3} pb={3}>
            <Box>
              <H2 mb={0}>Sharing</H2>
              <Paragraph m={0} mt={0} fontSize={1} color="gray.60">
                {[recordName, recordReference].filter(Boolean).join(" · ")}
              </Paragraph>
            </Box>

            {viewerLevel === "view" && (
              <Flex
                gap="12"
                p={2}
                bg="gray.5"
                border="1px solid"
                borderColor="blue.20"
                borderRadius={0}
              >
                <Flex
                  width="28px"
                  height="28px"
                  flexShrink={0}
                  borderRadius="50%"
                  bg="blue.50"
                  alignItems="center"
                  justifyContent="center"
                >
                  <HiLockClosed size={13} color={colors.blue[100]} />
                </Flex>
                <Box>
                  <Paragraph m={0} fontSize={1} fontWeight={600}>
                    You have view-only access
                  </Paragraph>
                  <Paragraph m={0} fontSize={1} color="gray.60" lineHeight={1.5}>
                    You can see all of this {noun}, but actions are turned off.
                    Changes are made by the primary owner
                    {primaryLabel ? ` — contact ${primaryLabel}` : ""} if
                    something needs doing.
                  </Paragraph>
                </Box>
              </Flex>
            )}

            {isLongList ? (
              <Flex
                alignItems="center"
                gap="10"
                px={2}
                py="12px"
                bg="gray.5"
                border="1px solid"
                borderColor="gray.100"
                borderRadius={0}
              >
                <SectionLabel>PRIMARY</SectionLabel>
                <Box flex={1}>
                  <Paragraph m={0} fontSize={1} fontWeight={600}>
                    {primaryLabel || "-"}
                  </Paragraph>
                </Box>
                <HiLockClosed size={11} color={colors.gray[50]} />
              </Flex>
            ) : (
              <Flex flexDirection="column" gap="8">
                <Flex justifyContent="space-between" alignItems="center">
                  <SectionLabel>PRIMARY</SectionLabel>
                  <Flex alignItems="center" gap="5">
                    <HiLockClosed size={11} color={colors.gray[50]} />
                    <Paragraph m={0} fontSize={0} color="gray.50">
                      Can't be changed here
                    </Paragraph>
                  </Flex>
                </Flex>
                <Box
                  bg="gray.5"
                  border="1px solid"
                  borderColor="gray.100"
                  borderRadius={0}
                >
                  {(
                    [
                      ["Division", primaryDivision],
                      ["Team", primaryTeam],
                    ] as const
                  ).map(([label, unit], i) => (
                    <Flex
                      key={label}
                      alignItems="center"
                      gap="10"
                      p="12px"
                      borderTop={i ? "1px solid" : undefined}
                      borderColor="gray.100"
                    >
                      <Paragraph m={0} width="62px" fontSize={0} color="gray.50">
                        {label}
                      </Paragraph>
                      <Box flex={1}>
                        <Paragraph m={0} fontSize={1} fontWeight={600}>
                          {unit?.display_name ?? "-"}
                        </Paragraph>
                      </Box>
                      <Paragraph m={0} fontSize={0} color="gray.60">
                        Owner
                      </Paragraph>
                    </Flex>
                  ))}
                </Box>
              </Flex>
            )}

            <Flex flexDirection="column" gap="10" flex={isLongList ? 1 : undefined}>
              <Flex justifyContent="space-between" alignItems="center">
                <SectionLabel>
                  SHARED WITH ·{" "}
                  {shares.length === 0
                    ? 0
                    : [
                        divisionCount && plural(divisionCount, "DIVISION"),
                        teamCount && plural(teamCount, "TEAM"),
                      ]
                        .filter(Boolean)
                        .join(", ")}
                </SectionLabel>
                {onAdd && !isGrantee && !isAdding && (
                  <Button
                    variant="blueText"
                    px={0}
                    height="auto"
                    fontWeight={600}
                    Icon={HiOutlinePlus}
                    iconSize="14px"
                    iconColor="blue.primary"
                    isDisabled={!canManage}
                    title={noPermissionTitle}
                    onClick={() => setIsAdding(true)}
                  >
                    Add division or team
                  </Button>
                )}
              </Flex>

              {isAdding && onAdd && (
                <AddShareForm
                  primaryDivisionId={primaryDivision?.id}
                  primaryTeamId={primaryTeam?.id}
                  primaryLabel={primaryLabel}
                  existingShares={shares}
                  noun={noun}
                  supportsLevels={supportsLevels}
                  isSubmitting={isSaving}
                  onCancel={() => setIsAdding(false)}
                  onSubmit={(added) => {
                    onAdd(added);
                    setIsAdding(false);
                  }}
                />
              )}

              {isLongList && (
                <Input
                  size="small"
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search shared divisions and teams"
                />
              )}

              {shares.length === 0 ? (
                <Paragraph
                  m={0}
                  px={2}
                  py="12px"
                  fontSize={1}
                  color="gray.50"
                  border="1px dashed"
                  borderColor="gray.30"
                  borderRadius={0}
                >
                  Not shared with any other division or team.
                </Paragraph>
              ) : (
                <Box
                  borderTop="1px solid"
                  borderColor="gray.100"
                  overflowY={isLongList ? "auto" : undefined}
                  maxHeight={isLongList ? "calc(100vh - 380px)" : undefined}
                >
                  {visibleGroups.map((group) => (
                    <Box
                      key={group.key}
                      borderBottom="1px solid"
                      borderColor="gray.100"
                    >
                      <Flex
                        alignItems="center"
                        gap="10"
                        px="12px"
                        py="10px"
                      >
                        <Box flex={1} minWidth={0}>
                          <Paragraph
                            m={0}
                            fontSize={1}
                            fontWeight={600}
                            ellipsisOverflow
                          >
                            {group.name}
                            {!group.share && (
                              <Paragraph as="span" m={0} fontSize={0} color="gray.50">
                                {" "}
                                · {group.isPrimary ? "Primary" : "Teams only"}
                              </Paragraph>
                            )}
                          </Paragraph>
                        </Box>
                        {group.share && levelControl(group.share)}
                        {group.share &&
                          removeButton(group.name, [group.share, ...group.teams])}
                      </Flex>

                      {group.teams.map((team) => (
                        <Flex
                          key={team.id}
                          alignItems="center"
                          gap="10"
                          pl={4}
                          pr="12px"
                          py="8px"
                        >
                          <Box flex={1} minWidth={0}>
                            <Paragraph m={0} fontSize={1} color="gray.60" ellipsisOverflow>
                              {team.name}
                              {viewerTeamIds.includes(team.id) && (
                                <Paragraph as="span" m={0} fontSize={0} color="gray.50">
                                  {" "}
                                  · your team
                                </Paragraph>
                              )}
                            </Paragraph>
                          </Box>
                          {levelControl(team)}
                          {removeButton(team.name, [team])}
                        </Flex>
                      ))}
                    </Box>
                  ))}
                  {visibleGroups.length === 0 && (
                    <Paragraph m={0} py={2} fontSize={1} color="gray.50">
                      No divisions or teams match.
                    </Paragraph>
                  )}
                </Box>
              )}
            </Flex>

          </Flex>

          {!isGrantee && (
            <Flex
              justifyContent="space-between"
              alignItems="center"
              px={4}
              py={2}
              borderTop="1px solid"
              borderColor="gray.100"
            >
              <Paragraph m={0} fontSize={0} color="gray.50">
                {onAdd || onChangeLevel || onRemove
                  ? "Changes apply immediately and are audit-logged."
                  : ""}
              </Paragraph>
              <Button onClick={handleClose}>Done</Button>
            </Flex>
          )}
        </Flex>
      </Slider>

      {onRemove && (
        <Modal
          isOpen={!!removing}
          onClose={() => setRemoving(null)}
          title={removing ? `Stop sharing with ${removing.name}?` : ""}
          content={
            removing
              ? `Members of ${removing.name}${
                  removing.shares.length > 1
                    ? ` and its ${plural(removing.shares.length - 1, "team")}`
                    : ""
                } will no longer be able to see ${[
                  recordName,
                  recordReference && `(${recordReference})`,
                ]
                  .filter(Boolean)
                  .join(" ")}.\n\nThe primary${
                  primaryLabel ? `, ${primaryLabel},` : ""
                } is unaffected.`
              : ""
          }
          primaryButtonText="Stop sharing"
          primaryButtonVariant="danger"
          secondaryButtonText="Cancel"
          hasSecondaryButton
          width="440px"
          onModalConfirmClick={() => {
            if (removing) onRemove(removing.shares);
            setRemoving(null);
          }}
        />
      )}
    </>
  );
};
